import {RefObject, useEffect, useEffectEvent} from 'react';

import {isNil} from 'ramda';

const SCROLL_THRESHOLD_PX = 560;

type InfiniteScrollListenerProps = {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  isFetching: boolean;
  isEnabled: boolean;
  axis?: 'vertical' | 'horizontal';
  thresholdPx?: number;
  onLoadMore: () => void;
};

export function InfiniteScrollListener(props: InfiniteScrollListenerProps) {
  const axis = props.axis ?? 'vertical';
  const thresholdPx = props.thresholdPx ?? SCROLL_THRESHOLD_PX;
  const loadMore = useEffectEvent(props.onLoadMore);

  // This DOM subscription needs a named useEffect with cleanup: it must reattach when fetching or
  // enabled changes to check a short page again. useOnMount would miss that follow-up check and a
  // container enabled after mount; derived state, an event handler, and a key reset cannot attach it.
  useEffect(
    function attachInfiniteScrollListener() {
      const scrollContainer = props.scrollContainerRef.current;
      if (isNil(scrollContainer) || !props.isEnabled) {
        return;
      }

      function handleScroll() {
        const container = props.scrollContainerRef.current;
        if (
          isNil(container) ||
          props.isFetching ||
          !props.isEnabled ||
          (axis === 'horizontal' && !container.clientWidth)
        ) {
          return;
        }

        const distance =
          axis === 'horizontal'
            ? container.scrollWidth - container.scrollLeft - container.clientWidth
            : container.scrollHeight - container.scrollTop - container.clientHeight;

        if (distance < thresholdPx) {
          loadMore();
        }
      }

      scrollContainer.addEventListener('scroll', handleScroll, {passive: true});
      handleScroll();

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    },
    [props.scrollContainerRef, props.isFetching, props.isEnabled, axis, thresholdPx]
  );

  return null;
}
